import { ElMessage } from 'element-plus'
import { i18n } from '@/i18n/index'

export const useCopy = (text) => {
  const tmpTextField: any = document.createElement('textarea')
  tmpTextField.textContent = text
  tmpTextField.setAttribute('style', 'position:absolute; right:200%;')
  document.body.appendChild(tmpTextField)
  tmpTextField.select()
  tmpTextField.setSelectionRange(0, 99999)
  document.execCommand('copy')
  tmpTextField.remove()
  ElMessage.success(i18n.global.t('common.copySuccess'))
  return []
}
/**
 * 将 IPv4 地址转换为 32 位整数
 * @param {string} ip - IPv4 地址 (如 "192.168.1.1")
 * @returns {number} 32 位整数
 */
function ipToInt(ip) {
    return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet), 0) >>> 0;
}

/**
 * 将 32 位整数转换为 IPv4 地址
 * @param {number} int - 32 位整数
 * @returns {string} IPv4 地址
 */
function intToIp(int) {
    return [
        (int >>> 24) & 0xFF,
        (int >>> 16) & 0xFF,
        (int >>> 8) & 0xFF,
        int & 0xFF
    ].join('.');
}

/**
 * 计算子网信息
 * @param {string} cidr - CIDR 表示法 (如 "192.168.1.0/24")
 * @returns {Object} 包含子网信息的对象
 */
export function calculateSubnetInfo(cidr) {
    // 分割 IP 和前缀长度
    const [ipStr, prefixStr] = cidr.split('/');
    const prefixLength = parseInt(prefixStr, 10);

    if (prefixLength < 0 || prefixLength > 32) {
        throw new Error(i18n.global.t('common.cidrPrefixLengthInvalid'));
    }

    // 转换为整数
    const ipInt = ipToInt(ipStr);

    // 计算掩码
    const maskInt = prefixLength === 0 ? 0 : (~0 << (32 - prefixLength)) >>> 0;

    // 计算网络地址
    const networkInt = ipInt & maskInt;

    // 计算广播地址
    const broadcastInt = networkInt | (~maskInt >>> 0);

    // 计算第一个和最后一个可用主机地址
    const firstHostInt = networkInt + 1;
    const lastHostInt = broadcastInt - 1;

    // 计算地址数量
    const totalAddresses = Math.pow(2, 32 - prefixLength);
    const usableHosts = Math.max(totalAddresses - 2, 0); // 对于 /31 和 /32 特殊处理

    return {
        // CIDR 表示
        cidr: cidr,

        // 基本网络信息
        networkAddress: intToIp(networkInt),
        broadcastAddress: intToIp(broadcastInt),
        subnetMask: intToIp(maskInt),
        prefixLength: prefixLength,

        // 主机范围
        firstUsableHost: intToIp(firstHostInt),
        lastUsableHost: intToIp(lastHostInt),

        // 完整的 IP 范围（含网络和广播）
        fullRangeStart: intToIp(networkInt),
        fullRangeEnd: intToIp(broadcastInt),

        // 可用主机范围
        usableRangeStart: intToIp(firstHostInt),
        usableRangeEnd: intToIp(lastHostInt),

        // 数量统计
        totalAddresses: totalAddresses,
        usableHosts: usableHosts,

        // 是否为特殊子网
        isPointToPoint: prefixLength >= 31, // /31 和 /32 用于点对点链接
        isSingleHost: prefixLength === 32
    };
}

/**
 * 获取 CIDR 的 IP 范围（简化版）
 * @param {string} cidr - CIDR 表示法
 * @param {boolean} usableOnly - 是否只返回可用主机范围
 * @returns {Object} 包含开始和结束地址的对象
 */
function getIPRange(cidr, usableOnly = false) {
    const info = calculateSubnetInfo(cidr);

    if (usableOnly) {
        return {
            start: info.usableRangeStart,
            end: info.usableRangeEnd,
            type: 'usable_hosts'
        };
    }

    return {
        start: info.fullRangeStart,
        end: info.fullRangeEnd,
        type: 'full_range'
    };
}

/**
 * 生成子网内所有 IP 地址
 * @param {string} cidr - CIDR 表示法
 * @param {boolean} excludeReserved - 是否排除网络和广播地址
 * @returns {string[]} IP 地址数组
 */
function generateAllIPs(cidr, excludeReserved = true) {
    const info = calculateSubnetInfo(cidr);
    const startIp = excludeReserved ? ipToInt(info.firstUsableHost) : ipToInt(info.networkAddress);
    const endIp = excludeReserved ? ipToInt(info.lastUsableHost) : ipToInt(info.broadcastAddress);

    const ips = [];
    for (let i = startIp; i <= endIp; i++) {
        ips.push(intToIp(i));
    }

    return ips;
}
